import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Button, Empty, Modal, Spin } from 'antd';
import { CheckOutlined, DownOutlined } from '@ant-design/icons';
import {
	TABLET_SELECT_TRIGGER_STYLE,
	TABLET_SELECT_OPTION_ROW_STYLE,
} from '../tabletStyles';

export interface TabletSelectOption {
	value: string | number;
	label: ReactNode;
	triggerLabel?: ReactNode;
	disabled?: boolean;
	extra?: ReactNode;
}

export interface TabletSelectModalProps {
	title: string;
	placeholder: string;
	value?: string | number | null;
	options: TabletSelectOption[];
	onChange: (value: string | number) => void;
	disabled?: boolean;
	loading?: boolean;
	error?: boolean;
	emptyText?: string;
	header?: ReactNode;
	/** Controlled open state — close from outside (e.g. before opening another modal). */
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
}

// Modal-based replacement for antd Select on tablet pages: dropdown lists
// chain their touch scroll to the page container, a modal list with
// overscroll-behavior: contain does not.
const TabletSelectModal = ({
	title,
	placeholder,
	value,
	options,
	onChange,
	disabled = false,
	loading = false,
	error = false,
	emptyText = 'No options',
	header,
	open: openProp,
	onOpenChange,
}: TabletSelectModalProps) => {
	const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
	const controlled = openProp !== undefined;
	const open = controlled ? openProp : uncontrolledOpen;
	const setOpen = (next: boolean) => {
		if (!controlled) setUncontrolledOpen(next);
		onOpenChange?.(next);
	};

	const selected =
		value !== null && value !== undefined
			? options.find((option) => option.value === value)
			: undefined;

	const triggerStyle: CSSProperties = {
		...TABLET_SELECT_TRIGGER_STYLE,
		...(error ? { borderColor: '#ff4d4f' } : {}),
	};

	const handleSelect = (option: TabletSelectOption) => {
		if (option.disabled) return;
		onChange(option.value);
		setOpen(false);
	};

	return (
		<>
			<Button
				size="large"
				style={triggerStyle}
				disabled={disabled}
				onClick={() => setOpen(true)}
			>
				<span
					style={{
						flex: 1,
						textAlign: 'left',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
						color: selected ? undefined : '#bfbfbf',
						fontWeight: 'normal',
					}}
				>
					{selected ? selected.triggerLabel ?? selected.label : placeholder}
				</span>
				{loading ? (
					<Spin size="small" />
				) : (
					<DownOutlined style={{ color: '#bfbfbf', fontSize: 14 }} />
				)}
			</Button>

			<Modal
				title={title}
				open={open}
				onCancel={() => setOpen(false)}
				footer={null}
				centered
				destroyOnClose
			>
				{header && (
					<div
						style={{
							paddingBottom: 12,
							marginBottom: 8,
							borderBottom: '1px solid #f0f0f0',
						}}
					>
						{header}
					</div>
				)}
				<div
					style={{
						maxHeight: '65vh',
						overflowY: 'auto',
						overscrollBehavior: 'contain',
						WebkitOverflowScrolling: 'touch',
					}}
				>
					{options.length === 0 ? (
						<Empty description={emptyText} style={{ padding: '24px 0' }} />
					) : (
						options.map((option) => {
							const isSelected = option.value === value;
							return (
								<div
									key={option.value}
									role="button"
									aria-disabled={option.disabled || undefined}
									onClick={() => handleSelect(option)}
									style={{
										...TABLET_SELECT_OPTION_ROW_STYLE,
										...(isSelected ? { background: '#e6f7ff' } : {}),
										...(option.disabled
											? { color: '#bfbfbf', cursor: 'not-allowed' }
											: {}),
									}}
								>
									<div style={{ flex: 1, minWidth: 0 }}>{option.label}</div>
									{isSelected && (
										<CheckOutlined
											style={{ color: '#1890ff', fontSize: 18 }}
										/>
									)}
									{option.extra && (
										<div onClick={(e) => e.stopPropagation()}>
											{option.extra}
										</div>
									)}
								</div>
							);
						})
					)}
				</div>
			</Modal>
		</>
	);
};

export default TabletSelectModal;
